import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users2, Smartphone, ShieldCheck, FileText, TrendingUp, ScrollText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import MakeReferrerButton from "@/components/MakeReferrerButton";

const ROLES = ["customer", "b2b_agent", "b2b_admin", "support", "logistics", "admin"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);

  const load = useCallback(async () => {
    await Promise.all([
      api.get("/admin/stats").then(r => setStats(r.data)),
      api.get("/admin/users").then(r => setUsers(r.data)),
      api.get("/admin/audit").then(r => setAudit(r.data)),
    ]);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (uid, role) => {
    await api.post(`/admin/users/${uid}/role`, { role });
    toast.success("Role updated");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Admin</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight">Platform overview</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "claims", label: "Export claims" },
            { key: "users", label: "Export users" },
            { key: "subscriptions", label: "Export subs" },
          ].map(x => (
            <Button
              key={x.key}
              variant="outline"
              size="sm"
              data-testid={`admin-export-${x.key}`}
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${x.key}.csv`, "_blank")}
            >
              <Download className="w-3.5 h-3.5 mr-2" /> {x.label}
            </Button>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {[
            { icon: Users2, label: "Users", value: stats.total_users, tid: ADMIN.statsUsers },
            { icon: Smartphone, label: "Devices", value: stats.total_devices },
            { icon: ShieldCheck, label: "Active subs", value: stats.active_subscriptions },
            { icon: FileText, label: "Claims", value: stats.total_claims, tid: ADMIN.statsClaims },
            { icon: FileText, label: "Open claims", value: stats.open_claims },
            { icon: TrendingUp, label: "Revenue", value: `£${stats.revenue_gbp.toLocaleString()}`, tid: ADMIN.statsRevenue },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} data-testid={s.tid} className="bg-card border border-border rounded-2xl p-4">
                <div className="w-8 h-8 rounded-lg bg-primary/8 text-primary flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="mt-3 overline text-muted-foreground">{s.label}</div>
                <div className="font-display text-2xl mt-1">{s.value}</div>
              </div>
            );
          })}
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="claims">Claim mix</TabsTrigger>
          <TabsTrigger value="audit">Audit log</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="pt-6">
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border text-[10px] overline text-muted-foreground">
              <div className="col-span-3">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2 text-right">Set role</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {users.map(u => (
              <div key={u.id} data-testid={ADMIN.userRow(u.id)} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-0 items-center text-sm">
                <div className="col-span-3 truncate font-medium">{u.name}</div>
                <div className="col-span-4 truncate text-muted-foreground">{u.email}</div>
                <div className="col-span-2 text-xs capitalize">{u.role?.replace("_"," ")}</div>
                <div className="col-span-2 flex justify-end">
                  <Select value={u.role} onValueChange={(r) => setRole(u.id, r)}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 flex justify-end">
                  <MakeReferrerButton user={u} />
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="plans" className="pt-6">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {["seeker", "hunter", "vault", "household"].map(p => (
                <div key={p} className="bg-card border border-border rounded-2xl p-6">
                  <div className="overline text-secondary capitalize">{p}</div>
                  <div className="mt-2 font-display text-4xl">{stats.plan_distribution[p] || 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">Active subscriptions</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="claims" className="pt-6">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(stats.claim_distribution).map(([k, v]) => (
                <div key={k} className="bg-card border border-border rounded-xl p-4">
                  <div className="overline text-muted-foreground capitalize">{k.replace("_"," ")}</div>
                  <div className="font-display text-2xl mt-1">{v}</div>
                </div>
              ))}
              {Object.keys(stats.claim_distribution).length === 0 && (
                <div className="col-span-full text-sm text-muted-foreground">No claims yet.</div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="audit" className="pt-6">
          <div className="bg-card border border-border rounded-2xl divide-y divide-border">
            {audit.length === 0 && <div className="p-6 text-sm text-muted-foreground">No audit events yet.</div>}
            {audit.map(a => (
              <div key={a.id} className="p-4 flex items-start gap-3">
                <ScrollText className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-medium">{a.action} <span className="text-muted-foreground text-xs">· {a.entity}</span></div>
                  <div className="text-xs text-muted-foreground">{new Date(a.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
